import { FileSignature } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { canViewModule } from '@/auth/modulePermission'
import { useSessionPermissions } from '@/auth/useCanEditModule'

/**
 * Where the term sheet builder opens on one deal. `?loan=`, not `?deal=`:
 * `?deal=` is the dossier overlay's own deep link and would reopen it on top
 * of the builder.
 */
export function termSheetHref(loanId: string): string {
  return `/term-sheet?loan=${encodeURIComponent(loanId)}`
}

/**
 * The dossier's way into the term sheet builder for the deal it shows (C27d:
 * "term sheet off any deal"). Offered only to a role that can open Term
 * Sheets — the same rule the sidebar applies; the API stays authoritative.
 * Read from the session's own grants (/auth/me for an Entra session, which
 * has no local profile).
 *
 * `onLeave` closes the dossier first and may refuse (unsaved edits), in which
 * case nothing navigates.
 */
export function TermSheetEntryButton({ loanId, onLeave }: { loanId: string; onLeave: () => boolean }) {
  const sessionPermissions = useSessionPermissions()
  const navigate = useNavigate()
  if (!loanId || !canViewModule(sessionPermissions, 'term-sheet')) return null
  return (
    <button
      type="button"
      onClick={() => {
        if (!onLeave()) return
        navigate(termSheetHref(loanId))
      }}
      aria-label="Open the term sheet for this deal"
      title="Term sheet — build the indicative summary and long-form terms for this deal"
      className="inline-flex h-7 w-7 items-center justify-center rounded text-amber-300 transition-colors hover:bg-amber-300/10 hover:text-amber-200 focus-visible:outline focus-visible:outline-amber-300"
    >
      <FileSignature size={16} aria-hidden="true" />
    </button>
  )
}

export default TermSheetEntryButton
